import React, { useState, useEffect } from "react";
import {
  Card,
  Table,
  Button,
  Tag,
  Space,
  Input,
  Select,
  DatePicker,
  Tooltip,
  Badge,
  Row,
  Col,
  Statistic,
} from "antd";
import {
  PlusOutlined,
  SendOutlined,
  TruckOutlined,
  EyeOutlined,
  ClockCircleOutlined,
  WarningOutlined,
  CheckCircleOutlined,
  SyncOutlined,
} from "@ant-design/icons";
import { Navigate, useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import { getOutsourceJobs, getOverdueJobs } from "../../api/outsourceApi";
import CreateOutsourceModal from "./CreateOutsourceModal";
import SendToVendorModal from "./SendToVendorModal";
import RecordDeliveryModal from "./RecordDeliveryModal";
import PageTitle from "../page-header/PageHeader";
import checkTokenExp from "../../utils/checkTokenExp";

const { Search } = Input;

const OutsourceJobList = () => {
  const navigate = useNavigate();
  const isLogged = Boolean(localStorage.getItem("isLogged"));

  const [loading, setLoading] = useState(false);
  const [outsourceJobs, setOutsourceJobs] = useState([]);
  const [total, setTotal] = useState(0);
  const [overdueCount, setOverdueCount] = useState(0);
  const [filters, setFilters] = useState({
    status: undefined,
    vendor_id: undefined,
    service_type: undefined,
    page: 1,
    count: 20,
  });
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [sendModalVisible, setSendModalVisible] = useState(false);
  const [deliveryModalVisible, setDeliveryModalVisible] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);

  useEffect(() => {
    const accessToken = localStorage.getItem("access-token");
    checkTokenExp(accessToken);
    fetchData();
  }, [filters]);

  useEffect(() => {
    fetchOverdue();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const data = await getOutsourceJobs(filters);
      setOutsourceJobs(data.outsource_jobs || []);
      setTotal(data.total || 0);
    } catch (error) {
      toast.error("Failed to load outsource jobs");
    }
    setLoading(false);
  };

  const fetchOverdue = async () => {
    try {
      const data = await getOverdueJobs();
      setOverdueCount(
        Array.isArray(data.outsource_jobs)
          ? data.outsource_jobs.length
          : data.total || 0
      );
    } catch {
      /* ignore */
    }
  };

  const handleSendToVendor = (job) => {
    setSelectedJob(job);
    setSendModalVisible(true);
  };

  const handleRecordDelivery = (job) => {
    setSelectedJob(job);
    setDeliveryModalVisible(true);
  };

  const getStatusConfig = (status) => {
    const configs = {
      created: { color: "default", text: "Created" },
      sent: { color: "processing", text: "Sent" },
      in_progress: { color: "blue", text: "In Progress" },
      partial_received: { color: "cyan", text: "Partial Received" },
      received: { color: "orange", text: "Received" },
      qc_passed: { color: "lime", text: "QC Passed" },
      qc_failed: { color: "red", text: "QC Failed" },
      completed: { color: "green", text: "Completed" },
      cancelled: { color: "default", text: "Cancelled" },
    };
    return configs[status] || { color: "default", text: status };
  };

  const columns = [
    {
      title: "Outsource #",
      dataIndex: "outsource_number",
      key: "outsource_number",
      render: (text, record) => (
        <a onClick={() => navigate(`/outsource/${record.id}`)}>{text}</a>
      ),
    },
    {
      title: "Job",
      dataIndex: ["job", "job_number"],
      key: "job",
      render: (text, record) => (
        <div>
          <div>{text || "-"}</div>
          <small style={{ color: "#888" }}>
            {record.job?.customer?.name}
          </small>
        </div>
      ),
    },
    {
      title: "Vendor",
      dataIndex: ["vendor", "name"],
      key: "vendor",
    },
    {
      title: "Service",
      dataIndex: "service_type",
      key: "service_type",
      render: (type) => {
        const colorMap = {
          printing: "blue",
          lamination: "purple",
          casting: "orange",
          corrugation: "green",
          pasting: "magenta",
        };
        return (
          <Tag color={colorMap[type] || "default"}>
            {type?.replace(/_/g, " ").toUpperCase()}
          </Tag>
        );
      },
    },
    {
      title: "Quantity",
      key: "quantity",
      render: (_, record) => {
        const sent = record.sent_quantity || 0;
        const received = record.total_received || 0;
        return (
          <div>
            <div>Sent: {sent.toLocaleString()}</div>
            <div style={{ color: "#52c41a" }}>
              Received: {received.toLocaleString()}
            </div>
          </div>
        );
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => {
        const config = getStatusConfig(status);
        return <Tag color={config.color}>{config.text}</Tag>;
      },
    },
    {
      title: "Dates",
      key: "dates",
      render: (_, record) => (
        <div style={{ fontSize: 12 }}>
          <div>
            Sent:{" "}
            {record.sent_date
              ? dayjs(record.sent_date).format("DD MMM")
              : "-"}
          </div>
          <div>
            Expected:{" "}
            {record.expected_date
              ? dayjs(record.expected_date).format("DD MMM")
              : "-"}
            {record.expected_date &&
              dayjs(record.expected_date).isBefore(dayjs()) &&
              !["completed", "cancelled", "qc_passed"].includes(record.status) && (
                <Badge
                  status="error"
                  text="Overdue"
                  style={{ marginLeft: 4, fontSize: 11 }}
                />
              )}
          </div>
        </div>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Space>
          <Tooltip title="View Details">
            <Button
              size="small"
              icon={<EyeOutlined />}
              onClick={() => navigate(`/outsource/${record.id}`)}
            />
          </Tooltip>

          {record.status === "created" && (
            <Tooltip title="Send to Vendor">
              <Button
                size="small"
                type="primary"
                icon={<SendOutlined />}
                onClick={() => handleSendToVendor(record)}
              />
            </Tooltip>
          )}

          {["sent", "in_progress", "partial_received"].includes(
            record.status
          ) && (
            <Tooltip title="Record Delivery">
              <Button
                size="small"
                icon={<TruckOutlined />}
                onClick={() => handleRecordDelivery(record)}
              />
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  if (!isLogged) {
    return <Navigate to="/auth/login" replace />;
  }

  return (
    <div>
      <PageTitle title="Back" />
      <Card
        title={
          <span>
            <TruckOutlined style={{ marginRight: 8 }} />
            Outsource Job Work
          </span>
        }
        extra={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setCreateModalVisible(true)}
          >
            New Outsource Job
          </Button>
        }
      >
        {/* Summary stats */}
        <Row gutter={16} style={{ marginBottom: 20 }}>
          <Col span={6}>
            <Card size="small">
              <Statistic
                title="Total"
                value={total}
                prefix={<SyncOutlined />}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card size="small">
              <Statistic
                title="In Progress"
                value={
                  outsourceJobs.filter((j) =>
                    ["sent", "in_progress"].includes(j.status)
                  ).length
                }
                prefix={<ClockCircleOutlined />}
                valueStyle={{ color: "#1890ff" }}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card size="small">
              <Statistic
                title="Overdue"
                value={overdueCount}
                prefix={<WarningOutlined />}
                valueStyle={{ color: overdueCount > 0 ? "#ff4d4f" : "#52c41a" }}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card size="small">
              <Statistic
                title="Completed"
                value={
                  outsourceJobs.filter((j) => j.status === "completed").length
                }
                prefix={<CheckCircleOutlined />}
                valueStyle={{ color: "#52c41a" }}
              />
            </Card>
          </Col>
        </Row>

        {/* Filters */}
        <Space style={{ marginBottom: 16 }} wrap>
          <Search
            placeholder="Search by job/vendor"
            allowClear
            style={{ width: 200 }}
            onSearch={(value) =>
              setFilters({ ...filters, search: value, page: 1 })
            }
          />
          <Select
            style={{ width: 160 }}
            placeholder="Status"
            allowClear
            value={filters.status}
            onChange={(value) =>
              setFilters({ ...filters, status: value, page: 1 })
            }
          >
            <Select.Option value="created">Created</Select.Option>
            <Select.Option value="sent">Sent</Select.Option>
            <Select.Option value="in_progress">In Progress</Select.Option>
            <Select.Option value="partial_received">
              Partial Received
            </Select.Option>
            <Select.Option value="received">Received</Select.Option>
            <Select.Option value="qc_passed">QC Passed</Select.Option>
            <Select.Option value="qc_failed">QC Failed</Select.Option>
            <Select.Option value="completed">Completed</Select.Option>
          </Select>
          <Select
            style={{ width: 160 }}
            placeholder="Service Type"
            allowClear
            value={filters.service_type}
            onChange={(value) =>
              setFilters({ ...filters, service_type: value, page: 1 })
            }
          >
            <Select.Option value="printing">Printing</Select.Option>
            <Select.Option value="lamination">Lamination</Select.Option>
            <Select.Option value="casting">Casting</Select.Option>
            <Select.Option value="corrugation">Corrugation</Select.Option>
            <Select.Option value="pasting">Pasting</Select.Option>
          </Select>
        </Space>

        <Table
          columns={columns}
          dataSource={outsourceJobs}
          rowKey="id"
          loading={loading}
          pagination={{
            current: filters.page,
            pageSize: filters.count,
            total,
            showSizeChanger: true,
            showTotal: (t, range) => `${range[0]}-${range[1]} of ${t}`,
            onChange: (page, count) =>
              setFilters({ ...filters, page, count }),
          }}
        />
      </Card>

      {/* Modals */}
      <CreateOutsourceModal
        visible={createModalVisible}
        onCancel={() => setCreateModalVisible(false)}
        onSuccess={() => {
          setCreateModalVisible(false);
          fetchData();
        }}
      />

      <SendToVendorModal
        visible={sendModalVisible}
        outsourceJob={selectedJob}
        onCancel={() => setSendModalVisible(false)}
        onSuccess={() => {
          setSendModalVisible(false);
          fetchData();
        }}
      />

      <RecordDeliveryModal
        visible={deliveryModalVisible}
        outsourceJob={selectedJob}
        onCancel={() => setDeliveryModalVisible(false)}
        onSuccess={() => {
          setDeliveryModalVisible(false);
          fetchData();
        }}
      />
    </div>
  );
};

export default OutsourceJobList;
